// Quantum Transport: the desktop's parameter forms (from the API) and their validation.
import { useQuery } from "@tanstack/react-query";
import { get, post } from "./api";
import type { DeviceForms } from "./device";

export type Raw = string | boolean;

export interface TransportTab {
  key: string;
  label: string;
  material: string;
  preset: string | null;
  note: string;
  modes: string[];            // the modes this tab offers (Device Characterization and T1 Simulation: 2D tabs only)
  references?: Record<string, number[]>;   // mode -> numbers of the papers it is based on ("Energy Bands [1]")
}

/** One entry of the References list (the desktop's References menu, qdtransport/references/*.bib). */
export interface Reference {
  number: number;
  key: string;
  citation: string;           // APS style, without the link
  url: string;                // DOI link ("" if none)
  tags: string[];
  used_for?: string[];        // what it is the source for (SKFitter, QTetris; the References page shows it)
}

/** "[1]" / "[1,2]" for a mode's reference numbers, "" when it has none (the desktop's dropdown marker). */
export function refMarker(numbers: number[] | undefined): string {
  return numbers?.length ? `[${numbers.join(",")}]` : "";
}

export interface TransportSpec {
  name: string;
  label: string;
  unit: string;
  kind: "float" | "int" | "bool";
  min: number | null;
  max: number | null;
  group: string;
  help: string;
  derived: boolean;
  auto: boolean;
  default: number | boolean;
}

export interface TransportForms {
  tabs: TransportTab[];
  modes: { key: string; label: string }[];
  specs: Record<string, Record<string, TransportSpec[]>>;       // Energy Bands / Transport Simulation forms
  preview_steps: number;
  device: DeviceForms;                                          // Device Characterization / T1 Simulation forms
  references?: Reference[];                                     // the References list, numbered
}

export interface LimitInfo {
  detail: string;
  limit: string;
  allowed: number;
  requested: number;
}

export interface Resolved {
  ok: boolean;
  errors: Record<string, string>;
  params: Record<string, number | boolean> | null;
  inputs: Record<string, number | boolean> | null;
  auto: Record<string, number | boolean>;
  limit: LimitInfo | null;
}

/** What a project remembers of the form (in project.settings.transport) and a saved parameter set. */
export interface FormMemory {
  tab: string;
  mode: string;
  edited: Record<string, Raw>;
}
export interface SavedSet extends FormMemory {
  name: string;
  saved_at: string;
}

export function useTransportForms() {
  return useQuery({ queryKey: ["transport-forms"], queryFn: () => get<TransportForms>("/transport/forms"), staleTime: Infinity });
}

export function resolveForm(tab: string, mode: string, values: Record<string, Raw>) {
  return post<Resolved>("/transport/resolve", { tab, mode, values });
}

/** A value as the desktop shows it in a field (10 significant digits, no trailing zeros). */
export function formatValue(v: unknown): Raw {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(Number(v.toPrecision(10)));
  return v == null ? "" : String(v);
}

/** Group the specs as on the desktop, keeping their order. */
export function groupSpecs(specs: TransportSpec[]): [string, TransportSpec[]][] {
  const groups: [string, TransportSpec[]][] = [];
  for (const s of specs) {
    const g = groups.find(([name]) => name === s.group);
    if (g) g[1].push(s);
    else groups.push([s.group, [s]]);
  }
  return groups;
}

/** Largest square map (steps per axis) within a plan's point limit. */
export function squareWithin(points: number): number {
  return Math.max(2, Math.floor(Math.sqrt(points)));
}
